import { Modal, Button, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useState } from 'react'
import { useUser } from '@supabase/auth-helpers-react'
import { IconPencil, IconTrash } from '@tabler/icons-react'

import ProfileAvatar from './ProfileAvatar'
import CommentContent from './CommentContent'
import CommentComposer from './CommentComposer'

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

export default function CommentItem({
    comment,
    profilesMap,
    profiles,
    updateComment: updateCommentApi,
    deleteComment: deleteCommentApi,
}) {
    const user = useUser()
    const isCommentOwner = user?.id === comment.profile?.id

    const [isEditing, setIsEditing] = useState(false)
    const [editContent, setEditContent] = useState(comment.content)
    const [isSaving, setIsSaving] = useState(false)

    const startEditing = () => {
        setEditContent(comment.content)
        setIsEditing(true)
    }

    const cancelEditing = () => {
        setIsEditing(false)
    }

    const handleUpdate = async () => {
        setIsSaving(true)

        try {
            await updateCommentApi(comment.id, editContent)
            setIsEditing(false)
        } catch {
            // Error handled by useAxiosApi
        }

        setIsSaving(false)
    }

    // Delete
    const [openDeleteModal, { open: openDelete, close: closeDelete }] =
        useDisclosure(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDelete = async () => {
        setIsDeleting(true)

        try {
            await deleteCommentApi(comment.id)
            closeDelete()
        } catch {
            // Error handled by useAxiosApi
        }

        setIsDeleting(false)
    }

    // Date display
    const dateToDisplay =
        comment.updated_at > comment.created_at ? 'updated_at' : 'created_at'
    const isEdited = dateToDisplay === 'updated_at'
    const date = dayjs(comment[dateToDisplay])
    const dateFormatted = date.isBefore(dayjs().subtract(10, 'day'))
        ? date.format('ll à HH:mm')
        : date.fromNow()
    const tooltip = `${isEdited ? 'Modifié' : 'Publié'} le ${date.format('dddd LL à HH:mm')}`

    return (
        <>
            <div className="rounded-xl px-4 py-3 -mx-4 hover:bg-gray-50 transition-colors">
                <div className="flex gap-3">
                    <div className="shrink-0 pt-0.5">
                        {comment.profile && (
                            <ProfileAvatar
                                profile={comment.profile}
                                size={32}
                                noHoverCard
                            />
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                            <span className="text-sm font-medium text-gray-900">
                                {comment.profile?.name}
                            </span>
                            <Tooltip label={tooltip}>
                                <span className="text-xs text-gray-400 cursor-default">
                                    {dateFormatted}
                                    {isEdited && ' (modifié)'}
                                </span>
                            </Tooltip>
                        </div>

                        {isEditing ? (
                            <div className="mt-2">
                                <CommentComposer
                                    value={editContent}
                                    onChange={setEditContent}
                                    onSubmit={handleUpdate}
                                    onCancel={cancelEditing}
                                    profiles={profiles}
                                    profilesMap={profilesMap}
                                    placeholder="Modifier le commentaire..."
                                    submitLabel="Enregistrer"
                                    isEditing
                                    isSaving={isSaving}
                                />
                            </div>
                        ) : (
                            <>
                                <div className="mt-1 text-sm text-gray-600 leading-relaxed">
                                    <CommentContent
                                        content={comment.content}
                                        profilesMap={profilesMap}
                                    />
                                </div>
                                {isCommentOwner && (
                                    <div className="flex items-center gap-1 mt-2">
                                        <Tooltip label="Modifier">
                                            <button
                                                onClick={startEditing}
                                                className="p-2 rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                                            >
                                                <IconPencil size={18} />
                                            </button>
                                        </Tooltip>
                                        <Tooltip label="Supprimer">
                                            <button
                                                onClick={openDelete}
                                                className="p-2 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                                            >
                                                <IconTrash size={18} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

            </div>

            <Modal
                opened={openDeleteModal}
                onClose={closeDelete}
                title="Supprimer le commentaire"
                size="sm"
            >
                <div className="flex flex-col">
                    <p className="text-sm text-gray-600">
                        Voulez-vous supprimer ce commentaire ?
                    </p>

                    <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-500">
                        <CommentContent
                            content={comment.content}
                            profilesMap={profilesMap}
                        />
                    </div>

                    <div className="grid grid-cols-2 mt-4 gap-3">
                        <Button variant="default" onClick={closeDelete}>
                            Annuler
                        </Button>
                        <Button
                            color="red"
                            loading={isDeleting}
                            onClick={handleDelete}
                        >
                            Supprimer
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    )
}
